import { Dialog } from "@mui/material";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { IconButton } from "@mui/material";
import { useToast } from "../../../../../Hooks/toast";
import { createPixChargeback } from "../../../../../Services/baasPixApi";
import { formatCurrency } from "../../../../../Utils/formatters";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-md";

const BTN_PRIMARY = "btn-primary inline-flex items-center gap-2";

const resolvePartnerTransactionId = (transaction) => {
  const meta = transaction?.metadata || {};
  const candidates = [
    meta.partner_transaction_id,
    meta.id_transaction,
    meta.external_id,
    transaction?.raw?.metadata?.partner_transaction_id,
    transaction?.raw?.metadata?.id_transaction,
    transaction?.raw?.external_id,
  ];
  for (const value of candidates) {
    const n = Number(String(value || "").replace(/\D/g, ""));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const resolveAmountCents = (transaction) => {
  const n = Math.abs(Number(transaction?.amount ?? 0));
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n >= 100 || Number.isInteger(n)) return Math.round(n);
  return Math.round(n * 100);
};

export const BaasTransactionRefundModal = ({ open, transaction, onClose, onSuccess }) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setConfirmed(false);
      setFormError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!transaction || loading) return;

    setLoading(true);
    setFormError(null);

    const accountId = String(transaction.accountId || "").trim();
    const idTransaction = resolvePartnerTransactionId(transaction);
    const amount = resolveAmountCents(transaction);

    if (!accountId || !idTransaction || amount <= 0) {
      setLoading(false);
      setFormError(
        intl.get("baas.transactions.refund.missingPartnerTx")
        || "Não foi possível identificar a transação Pix original para estorno.",
      );
      return;
    }

    try {
      await createPixChargeback(selectedInstance, accountId, {
        id_transaction: idTransaction,
        amount,
        reason_code: "MD06",
      });
      addToast({
        type: "success",
        title: intl.get("baas.transactions.refund.success"),
      });
      onSuccess?.();
    } catch (err) {
      setFormError(
        err?.detail
        || err?.message
        || intl.get("baas.transactions.refund.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!transaction) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
        <div>
          <h3 className="text-base font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.transactions.refund.title")}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.transactions.refund.subtitle")}
          </p>
        </div>
        <IconButton
          aria-label={intl.get("words.close")}
          onClick={onClose}
          size="small"
          disabled={loading}
        >
          <MdClose />
        </IconButton>
      </div>

      <div className="px-4 py-4">
        {formError ? (
          <p
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            {intl.get("baas.transactions.refund.warning")}
          </p>
        </div>

        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500 dark:text-gray-400">
              {intl.get("baas.transactions.detail.id")}
            </dt>
            <dd className="font-mono text-xs text-brand-dark dark:text-gray-100">
              {transaction.referenceId}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500 dark:text-gray-400">
              {intl.get("baas.transactions.columns.amount")}
            </dt>
            <dd className="font-medium text-brand-dark dark:text-gray-100">
              {formatCurrency(transaction.amount, transaction.currency)}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500 dark:text-gray-400">
              {intl.get("baas.transactions.columns.client")}
            </dt>
            <dd className="text-brand-dark dark:text-gray-100">{transaction.clientName}</dd>
          </div>
        </dl>

        <label className="mt-4 flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={loading}
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {intl.get("baas.transactions.refund.confirmLabel")}
          </span>
        </label>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3 dark:border-brand-dark-border">
        <button
          type="button"
          className={BTN_PRIMARY}
          onClick={handleConfirm}
          disabled={!confirmed || loading}
        >
          {loading
            ? intl.get("baas.transactions.refund.processing")
            : intl.get("baas.transactions.refund.confirm")}
        </button>
      </div>
    </Dialog>
  );
};
